/**
 * ProductCard — the storefront listing card.
 *
 * Ported from `tw-components.jsx` (see `COMPONENTS.md:31` for the prop contract) and
 * rendered on the listing screen at `Dens N Dente Tailwind.dc.html:655`. Add
 * `className="pc-compact"` for 4-up listing density.
 *
 * Optional slots stay unrendered when their data is absent — `brand`, `sku`, `unit`,
 * `rating`, `badge`. That's upstream behaviour, and it's what lets this app omit the
 * rating row (no data) while still showing `unit` on the ~99% of items that have a
 * Primary Sale Unit.
 *
 * Card hover follows the handoff exactly: the container gains `shadow-lg` but never
 * translates or scales. Image zoom, Quick Look and control-level hover feedback stay
 * independent of that container treatment.
 *
 * ─── Five deliberate divergences from upstream ─────────────────────────────────
 *
 * Divergences 4 and 5 are marked inline in the body. They mean this file is **no
 * longer byte-faithful** to `tw-components.jsx`, so the class-string drift check in
 * `design-system.md` → "Vendoring a component" will flag them; that is expected here
 * and nowhere else.
 *
 * 1. `media` node instead of a raw `<img>`. CLAUDE.md rule 2 forbids raw `<img>` in
 *    this app — it loses `ProductImage`'s `onError` fallback and NetSuite base-URL
 *    handling — and `design-system/` may not import from `common`, so the caller
 *    passes a rendered element. The upstream `image`-URL branch is kept as a fallback
 *    so the component still works standalone; give the node
 *    `w-full h-full object-cover` plus the hover-zoom classes to match.
 *
 * 2. Controlled quantity. Upstream holds `qty` in `useState(1)`. Here the listing's
 *    quantity is owned above the card (`ListProduct` → `useQuantityHandlers`, which
 *    also derives the buy-X-get-Y `actualQuantity`), so `qty`/`onQty` are accepted and
 *    internal state is only the uncontrolled fallback.
 *
 * 3. `disableWhenOut`, default **false**. Upstream hard-codes
 *    `disabled={stock === 'out'}`. This business backorders — the old card let you add
 *    out-of-stock items and `config/config.js` has a whole `quantityBackOrdered` path —
 *    so defaulting to upstream's behaviour would block a real revenue path. Opt in
 *    where out-of-stock genuinely means unorderable.
 *
 * 4. **sku and stock share one row**, stock right-aligned, with the rating dropping
 *    below them. Upstream stacks all three as separate `flex-col` children.
 *
 * 5. **`pc-inline-add`'s geometry is baked into the button** rather than applied by a
 *    CSS override. Upstream writes `dnd-pc__addwide` as `w-full h-[44px] mt-3` below
 *    the foot, then the listing screen reshapes it — `.dnd-pc__body` becomes a wrap
 *    row, `.dnd-pc__foot` becomes `display:contents`, and `.pc-inline-add` overrides
 *    the button to `width:auto; height:40px; padding:0 var(--space-3); gap:6px;
 *    font-size:var(--text-sm)` beside the stepper
 *    (`Dens N Dente Tailwind.dc.html:44-61`). This file already renders that final
 *    order in plain JSX, so the override chain has nothing to reorder; the computed
 *    values are written as Tailwind on the button instead. The rendered result is the
 *    same — only `pc-inline-add` as a *class* is absent, so don't pass it.
 *
 *    The `stock === 'out'` disabled styling comes across intact
 *    (`disabled:bg-neutral-200` etc.), it just needs `disableWhenOut` per divergence 3.
 *
 * `addQty` is the buy-X-get-Y adjusted quantity — `actualQuantity` from the listing,
 * where selecting 2 of a buy-2-get-1 puts 3 in the cart. It shows in the label as
 * "Add 3", following upstream's own `addLabel` handling; the icon-only round button
 * (no `withQuantity`) still has nowhere to put it, so there it stays in the accessible
 * name. Upstream has no concept of free goods.
 */
import React from "react";

import { AddToFavorite } from "./AddToFavorite.jsx";
import { Badge } from "./Badge.jsx";
import { PC_ADD_TRANSITION, PC_ICON_SPRING, STOCK } from "./constants";
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import { PriceTag } from "./PriceTag.jsx";
import { QuantityStepper } from "./QuantityStepper.jsx";
import { Rating } from "./Rating.jsx";

export function ProductCard({
  name,
  brand,
  sku,
  media,
  image,
  icon = "medical_services",
  price,
  originalPrice,
  unit,
  rating,
  reviews,
  stock = "in",
  badge,
  promo,
  withQuantity = false,
  qty: qtyProp,
  onQty,
  addQty,
  addLabel,
  favorite = false,
  onFavorite,
  onQuickLook,
  onAdd,
  onClick,
  disableWhenOut = false,
  style = {},
  className = "",
}) {
  // A product on promotion renders the SAME anatomy as the deals page: coloured promo
  // pill on the media, promo unit price with the list price struck, and a savings line.
  // (Upstream's Aug 2026 update — grep `promoCardMeta` in the reference for the shape:
  // `{ badgeBg, badgeIcon, badgeText, price, original, saveStr }`.)
  const pr = promo || null;
  const shownPrice = pr && pr.price !== undefined ? pr.price : price;
  const shownOrig = pr ? pr.original : originalPrice;
  const onSale = shownOrig !== undefined && shownOrig > shownPrice;
  const [internalQty, setInternalQty] = React.useState(1);
  const qty = qtyProp === undefined ? internalQty : qtyProp;
  const setQty = onQty || setInternalQty;
  const st = STOCK[stock] || STOCK.in;
  const addDisabled = disableWhenOut && stock === "out";
  const labelQty = addQty === undefined ? qty : addQty;

  const addAriaLabel =
    labelQty > 1 ? `Add ${labelQty} × ${name} to cart` : `Add ${name} to cart`;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (onAdd) onAdd(withQuantity ? qty : undefined);
  };

  // `withQuantity`: the labelled `dnd-pc__addwide` pill, on the stepper's row. Its
  // width, height, padding, gap and text size are `pc-inline-add`'s computed values
  // (divergence 5), not upstream's pre-override `w-full h-[44px] mt-[var(--space-3)]`.
  const addWideButton = (
    <button
      aria-label={addAriaLabel}
      disabled={addDisabled}
      onClick={handleAdd}
      className={cx(
        "dnd-pc__addwide group/aw inline-flex shrink-0 items-center justify-center gap-[6px] h-[40px] px-[var(--space-3)] border-0 rounded-full bg-orange-500 text-white cursor-pointer font-sans font-semibold text-sm",
        PC_ADD_TRANSITION,
        "enabled:hover:bg-orange-600 enabled:hover:shadow-brand enabled:active:scale-[0.97] disabled:bg-neutral-200 disabled:text-[var(--text-disabled)] disabled:cursor-not-allowed disabled:shadow-none"
      )}
    >
      <Icon
        name="add_shopping_cart"
        size={18}
        weight={400}
        className={cx(
          PC_ICON_SPRING,
          "group-hover/aw:rotate-[-9deg] group-hover/aw:scale-[1.08]"
        )}
      />
      {addLabel
        ? labelQty > 1
          ? `${addLabel} ${labelQty}`
          : addLabel
        : `Add${labelQty > 1 ? ` ${labelQty}` : ""} to cart`}
    </button>
  );

  // Upstream renders this span UNCONDITIONALLY — `invisible` placeholder when there's
  // no promo — so the price block is the same height on every card in a row and the
  // foot never mis-aligns between promoted and plain neighbours. Shared by both foot
  // branches below (upstream has one foot; this file's two-branch split is Divergence 5).
  const saveLine = (
    <span
      aria-hidden={!(pr && pr.saveStr)}
      className={cx(
        "dnd-pc__save inline-flex items-center gap-[5px] mt-[3px] text-success-700 text-2xs font-semibold",
        !(pr && pr.saveStr) && "invisible"
      )}
    >
      <Icon name="savings" size={14} fill />
      {pr && pr.saveStr ? pr.saveStr : "\u00a0"}
    </span>
  );

  // No `withQuantity`: upstream's 44px round icon button, sitting opposite the price.
  // Dormant in this app — the listing is the only caller and it passes `withQuantity`.
  const addButton = (
    <button
      aria-label={addAriaLabel}
      disabled={addDisabled}
      onClick={handleAdd}
      className={cx(
        "dnd-pc__add group/add w-[44px] h-[44px] shrink-0 border-0 rounded-full bg-orange-500 text-white cursor-pointer inline-flex items-center justify-center",
        PC_ADD_TRANSITION,
        "enabled:hover:bg-orange-600 enabled:hover:shadow-brand enabled:active:scale-[0.88] disabled:cursor-not-allowed disabled:opacity-[0.55]"
      )}
    >
      <Icon
        name="add_shopping_cart"
        size={22}
        weight={400}
        className={cx(
          PC_ICON_SPRING,
          "group-hover/add:rotate-[-9deg] group-hover/add:scale-[1.08]"
        )}
      />
    </button>
  );

  return (
    <article
      style={style}
      className={cx(
        "dnd-pc group relative flex flex-col bg-card border border-line-subtle rounded-[var(--radius-card)] overflow-hidden font-sans",
        className
      )}
    >
      <div
        className="dnd-pc__media relative aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center"
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "default" }}
      >
        {media ||
          (image ? (
            <img
              className="dnd-pc__img w-full h-full object-cover [transition:transform_var(--duration-slow)_var(--ease-out)] group-hover:scale-105"
              src={image}
              alt={name}
            />
          ) : (
            <span className="dnd-pc__ph text-neutral-300">
              <Icon name={icon} size={72} weight={200} />
            </span>
          ))}
        <div className="dnd-pc__badges absolute top-[var(--space-3)] left-[var(--space-3)] flex gap-[6px]">
          {pr ? (
            <span
              className="dnd-pc__promopill inline-flex items-center gap-[5px] rounded-pill px-[11px] py-[6px] text-2xs font-bold tracking-[0.02em] text-white shadow-sm"
              style={{ background: pr.badgeBg }}
            >
              <Icon name={pr.badgeIcon} size={14} fill />
              {pr.badgeText}
            </span>
          ) : (
            onSale && (
              <Badge color="solid" size="sm">
                Sale
              </Badge>
            )
          )}
          {badge && (
            <Badge color="ocean" size="sm">
              {badge}
            </Badge>
          )}
        </div>
        {onFavorite && (
          <AddToFavorite
            saved={favorite}
            onToggle={onFavorite}
            className="dnd-pc__fav absolute top-[var(--space-2)] right-[var(--space-2)]"
          />
        )}
        {onQuickLook && (
          <button
            aria-label={`Quick look at ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              onQuickLook();
            }}
            className="dnd-pc__ql absolute bottom-[var(--space-3)] left-1/2 -translate-x-1/2 translate-y-[8px] opacity-0 group-hover:opacity-100 group-hover:translate-y-0 focus-visible:opacity-100 focus-visible:translate-y-0 inline-flex items-center gap-[6px] h-[36px] px-[var(--space-4)] border-0 rounded-pill bg-[var(--surface-glass)] backdrop-blur-[6px] text-ink font-sans text-[13px] font-semibold cursor-pointer shadow-md whitespace-nowrap [transition:opacity_var(--duration-base)_var(--ease-out),transform_var(--duration-base)_var(--ease-out),background_var(--duration-fast)] hover:bg-card"
          >
            <Icon name="visibility" size={17} weight={500} />
            Quick look
          </button>
        )}
      </div>
      {/* content-start: the body's wrap lines must pin to the top. Height-equalized cards
          otherwise distribute leftover height across every line, so one extra element (e.g.
          the promo savings line) nudges the name/SKU/rating out of alignment with the
          neighbouring cards. */}
      <div className="dnd-pc__body flex flex-col content-start gap-[var(--space-1)] pt-[var(--space-4)] px-[var(--space-4)] pb-[var(--space-5)] flex-1">
        {brand && (
          <span className="dnd-pc__brand text-2xs font-bold tracking-[var(--tracking-wider)] uppercase text-[var(--text-accent)]">
            {brand}
          </span>
        )}
        <h3
          className="dnd-pc__name text-base font-semibold text-ink leading-[1.35] mt-[2px] mb-0 [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden"
          onClick={onClick}
          style={{ cursor: onClick ? "pointer" : "default" }}
        >
          {name}
        </h3>
        {/* Divergence 4: upstream stacks sku, rating and stock as three separate
            flex-col children. Here sku and stock share one row with the stock
            right-aligned, which is what the design intends and what saves a line of
            height on a 198px-wide card. `ml-auto` rather than `justify-between` so
            the stock still sits right when a product has no sku. */}
        <div className="flex items-center gap-[var(--space-2)]">
          {sku && (
            <span className="dnd-pc__sku text-xs text-ink-3 min-w-0 truncate">
              {sku}
            </span>
          )}
          <span
            className={cx(
              "dnd-pc__stock ml-auto inline-flex shrink-0 items-center gap-[var(--space-1)] text-xs font-medium",
              st.cls
            )}
          >
            <Icon name={st.icon} size={14} weight={500} fill />
            {st.label}
          </span>
        </div>
        {rating !== undefined && (
          <Rating
            value={rating}
            count={reviews}
            size={15}
            readOnly
            style={{ marginTop: 4 }}
          />
        )}
        {/* Divergence 5: the price block stands alone and the stepper pairs with the
            labelled `dnd-pc__addwide` pill. That is the order the listing screen's own
            CSS produces (`Dens N Dente Tailwind.dc.html:44-61`), reached directly here
            instead of through `display:contents` + `order`. */}
        {withQuantity ? (
          <>
            <div className="dnd-pc__priceblock mt-[var(--space-3)] flex min-w-0 flex-col gap-[1px]">
              <PriceTag
                amount={shownPrice}
                original={shownOrig}
                size="md"
                className="flex-wrap"
              />
              {unit && (
                <span className="dnd-pc__unit text-xs text-ink-2 font-medium">
                  {unit}
                </span>
              )}
              {saveLine}
            </div>
            <div className="dnd-pc__foot mt-[var(--space-3)] flex items-center justify-between gap-[var(--space-2)]">
              <QuantityStepper value={qty} onChange={setQty} min={1} />
              {addWideButton}
            </div>
          </>
        ) : (
          <div className="dnd-pc__foot flex items-end justify-between gap-[var(--space-3)] mt-[var(--space-3)]">
            <div className="dnd-pc__priceblock flex-auto min-w-0 flex flex-col gap-[1px]">
              <PriceTag
                amount={shownPrice}
                original={shownOrig}
                size="md"
                className="flex-wrap"
              />
              {unit && (
                <span className="dnd-pc__unit text-xs text-ink-2 font-medium">
                  {unit}
                </span>
              )}
              {saveLine}
            </div>
            {addButton}
          </div>
        )}
      </div>
    </article>
  );
}
